import {
  DefaultContext,
  ErrorLike,
  NetworkStatus,
  type OperationVariables,
  type TypedDocumentNode,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type UsePollingOptions<TData, TVariables extends OperationVariables> = {
  /**
   * GraphQL query document
   */
  query: TypedDocumentNode<TData, TVariables>;
  /**
   * Polling interval in milliseconds
   */
  interval: number;
  /**
   * Maximum number of polling attempts before stopping
   */
  maxAttempts?: number;
  /**
   * Called when polling receives data successfully
   */
  onData?: (data: TData) => void;
  /**
   * Called when polling encounters an error
   */
  onError?: (error: ErrorLike) => void;
  /*
   * Called when max attempts are reached
   */
  onMaxAttemptsReached?: () => void;
  /**
   * Called when polling completes successfully (shouldStopPolling returns true)
   */
  onPollingCompleted?: (data: TData) => void;
  /**
   * Custom condition to determine if polling should stop
   * Return true to stop polling
   */
  shouldStopPolling?: (data: TData) => boolean | undefined;
  /**
   * Apollo Client fetch policy
   */
  fetchPolicy?: WatchQueryFetchPolicy;
  /**
   * Apollo Client context
   */
  context?: DefaultContext;
} & (keyof TVariables extends never
  ? { variables?: never }
  : Record<string, never> extends TVariables
    ? { variables?: TVariables }
    : { variables: TVariables });

export type UsePollingReturn<TData> = {
  /**
   * Current number of polling attempts
   */
  attempts: number;
  /**
   * Latest data from the query
   */
  data: TData | undefined;
  /**
   * Query error if any
   */
  error: Error | undefined;
  /**
   * Whether polling is currently active
   */
  isPolling: boolean;
  /**
   * Whether the query is loading
   */
  loading: boolean;
  /**
   * Start polling
   */
  startPolling: () => void;
  /**
   * Stop polling
   */
  stopPolling: () => void;
};

/**
 * Custom hook for polling GraphQL queries with configurable options and callbacks
 */
export function usePolling<TData, TVariables extends OperationVariables>(
  options: {
    query: TypedDocumentNode<TData, TVariables>;
  } & Omit<UsePollingOptions<TData, TVariables>, 'query'>,
): UsePollingReturn<TData>;

export function usePolling<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>({
  context,
  fetchPolicy = 'network-only',
  interval,
  maxAttempts = 5,
  onData,
  onError,
  onMaxAttemptsReached,
  onPollingCompleted,
  query,
  shouldStopPolling,
  variables,
}: UsePollingOptions<TData, TVariables>): UsePollingReturn<TData> {
  const [attempts, setAttempts] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const {
    data,
    error,
    loading,
    networkStatus,
    startPolling: _startPolling,
    stopPolling: _stopPolling,
  } = useQuery<TData, TVariables>(query, {
    context: { skipErrorNotification: true, ...context },
    fetchPolicy,
    skip: !isPolling,
    variables: variables as TVariables,
  });

  const callbacksRef = useRef({
    onData,
    onError,
    onMaxAttemptsReached,
    onPollingCompleted,
    shouldStopPolling,
  });

  useEffect(() => {
    callbacksRef.current = {
      onData,
      onError,
      onMaxAttemptsReached,
      onPollingCompleted,
      shouldStopPolling,
    };
  }, [onData, onError, onMaxAttemptsReached, onPollingCompleted, shouldStopPolling]);

  const startPolling = useCallback(() => {
    setAttempts(0);
    setIsPolling(true);
    _startPolling(interval);
  }, [_startPolling, interval]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    _stopPolling();
  }, [_stopPolling]);

  useEffect(() => {
    if (maxAttempts && attempts >= maxAttempts) {
      callbacksRef.current.onMaxAttemptsReached?.();
      stopPolling();
    }
  }, [attempts, maxAttempts, stopPolling]);

  useEffect(() => {
    if (!isPolling) return;

    if (networkStatus === NetworkStatus.error || networkStatus === NetworkStatus.ready) {
      setAttempts((current) => current + 1);
    }

    if (networkStatus === NetworkStatus.error) {
      if (error) callbacksRef.current.onError?.(error);
    }

    if (networkStatus === NetworkStatus.ready) {
      if (data) {
        callbacksRef.current.onData?.(data);

        if (callbacksRef.current.shouldStopPolling?.(data)) {
          callbacksRef.current.onPollingCompleted?.(data);
          stopPolling();
        }
      }
    }
  }, [data, error, isPolling, networkStatus, stopPolling]);

  return {
    attempts,
    data: data as TData | undefined,
    error: error as Error | undefined,
    isPolling,
    loading,
    startPolling,
    stopPolling,
  };
}
